import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  IconButton,
  Divider,
  MenuItem,
  Card,
} from "@mui/material";
import BasicDatePicker from "../../../components/ui/BasicDatePicker";
import PaperCard from "../../../components/ui/PaperCard";
import ModalPopup from "../../../components/ui/ModalPopup";
import ViewIcon from "./../../../assets/images/icons/eye.svg";
import DownloadIcon from "./../../../assets/images/icons/download.svg";
import EmailIcon from "./../../../assets/images/icons/mail.svg";
import PDFViewer from "../../../components/ui/PDFViewer";
import PdfPasswordInfo from "../../../components/ui/PdfPasswordInfo";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../services/redux/store";
import { Box, useMediaQuery, useTheme } from "@mui/system";
import dayjs from "dayjs"; // Import dayjs for date handling
import postsServices from "../../../services/api/service/postsService";
import {
  authSelector,
  userPermissionSelector,
} from "../../../services/redux/slices/authSlice";
import Toast from "../../../components/ui/Toast";
import LoaderBackdrop from "../../../components/ui/LoaderBackdrop";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import {
  documentsItOptions,
  messages,
  validStatuses,
} from "../../../data/constant";
import { InputField } from "../../../components/ui/Inputs";
import { dateToFinYearOptions } from "../../../utils/helpers";
import { dateParser } from "../../../utils/formatting";
import { decrypt } from "../../../utils/crypto";
import { styled } from "@mui/material/styles";
import HeadingLeftRight from "../../../components/ui/HeadingLeftRight";
import ReactGA from "react-ga4";
import isBetween from "dayjs/plugin/isBetween";
import { trackEvent } from "../../../utils/ga";
import AccountSelectorDropDown from "../../../components/ui/AccountSelectorDropDown";

interface RetrievedData {
  cifNumber?: string | null;
}

interface Payload {
  finReference: string;
  fromDate: string | null;
  toDate: string | null;
  type: string;
  cif: string;
  action: string;
}

export default function DocumentTerm() {
  const [viewPdfModal, setViewPdfModal] = useState<{
    isOpen: boolean;
    title: string;
  }>({ isOpen: false, title: "" });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [pdfUrlAccountStatement, setPdfUrlAccountStatement] =
    useState<string>("");
  const [lanNumber, setLanNumber] = useState("");
  const [toDate, setToDate] = useState<Date | null>(null);
  const { userProfile } = useSelector(authSelector);
  const { rolePermissions } = useSelector(userPermissionSelector);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [maxDate, setMaxDate] = useState<Date | null>(null);
  const [certificateInput, setCertificateInput] = useState<{
    certificate: string;
    years: string;
  }>({ certificate: "provsonintstcert", years: "" });
  const IconButton = styled("div")({
    color: "rgba(75, 70, 92, 1)",
    fontSize: "15px",
    display: "flex",
    float: "right",
  });
  const Typography = styled("div")({
    color: "#00b3b5",
    fontWeight: "500",
    marginLeft: "8px",
    marginRight: "5px",
    fontSize: "clamp(12px, 1.2vw, 13px)",
  });
  dayjs.extend(isBetween);
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const selectedLoanNumber = useSelector(
    (state: RootState) => state.dashboardData.selectedLoanNumber
  );

  const dashboardDataSet = useSelector(
    (state: RootState) => state.dashboardData.sanctionedDisbursedData
  );

  const isForeclosureAllowedValue = useMemo(() => {
    const matchedObject: any = dashboardDataSet.find(
      (data: any) => data.lanNumber === selectedLoanNumber
    );
    return matchedObject?.isForeclosureAllowed;
  }, [selectedLoanNumber, dashboardDataSet]);


  const isActive: any = dashboardDataSet.find(
    (data: any) => data.lanNumber === selectedLoanNumber
  )?.status;
  const loanAccountNumberOptions = useSelector(
    (state: RootState) => state.option.loanAccountNumberOptions
  );

  const selectedOption = useSelector(
    (state: RootState) => state.option.selectedOption
  );
  const [viewDetailsModal, setViewDetailsModal] = useState(false);

  useEffect(() => {
    setLanNumber(selectedLoanNumber);
    const matchedObject: any = dashboardDataSet?.find(
      (data: any) => data?.lanNumber === selectedLoanNumber
    );
    const loanStartDate = matchedObject?.loanStartDate
      ? new Date(matchedObject.loanStartDate)
      : null;

    if (loanStartDate && !isNaN(loanStartDate.getTime())) {
      setFromDate(loanStartDate);
      setMinDate(loanStartDate);
    } else {
      console.error("Invalid loanStartDate", loanStartDate);
    }

    const maturityDate = validStatuses.includes(matchedObject?.status)
      ? matchedObject?.maturityDate
        ? new Date(matchedObject?.maturityDate)
        : new Date()
      : new Date();

    if (!isNaN(maturityDate.getTime())) {
      setToDate(maturityDate);
      setMaxDate(maturityDate);
    } else {
      console.error("Invalid maturityDate", maturityDate);
    }
  }, [selectedLoanNumber, dashboardDataSet]);
  const finYearsOptions = useMemo(() => {
    const matchedObject: any = dashboardDataSet?.find(
      (data: any) => data?.lanNumber === selectedLoanNumber
    );
    let maturityDate;
    if (validStatuses.includes(matchedObject?.status)) {
      maturityDate = matchedObject?.maturityDate;
    } else {
      maturityDate = dayjs().format("YYYY-MM-DD");
    }

    const yearsOptions = dateToFinYearOptions(
      matchedObject?.loanStartDate,
      maturityDate
    );
    setCertificateInput({
      ...certificateInput,
      years: yearsOptions[yearsOptions.length - 1]?.value,
    });
    return yearsOptions;
  }, [selectedLoanNumber, dashboardDataSet]);

  const accountStatement = async (actionType: string, type: string) => {
    setIsLoading(true);
    try {
      const formattedFromDate = fromDate
        ? dayjs(fromDate).format("YYYY-MM-DD")
        : null;
      const formattedToDate = toDate
        ? dayjs(toDate).format("YYYY-MM-DD")
        : null;

      const matchedObject = dashboardDataSet.find(
        (data: any) => data.lanNumber === selectedLoanNumber
      );

      const payload: any = {
        finReference: selectedLoanNumber,
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        type: type,
        cif: decrypt(userProfile?.cifNumber),
        action: actionType,
        loanCat: "term",
      };

      if (type === "cert") {
        const isLeapYear = (year: any) => dayjs(`${year}-02-29`).isValid(); // Leap year check
        const formattedFromDatenew = formattedFromDate || "";
        const yearCustome = formattedFromDatenew.split("-")[0] || "1970";

        const startOfFinancialYear = `${certificateInput?.years}-04-01`; // Start of FY
        const endOfFinancialYear = `${+certificateInput?.years + 1}-03-31`; // End of FY

        // Strict validation for financial year
        if (
          dayjs(formattedFromDatenew).isValid() &&
          dayjs(formattedFromDatenew).isBetween(
            startOfFinancialYear,
            endOfFinancialYear,
            null,
            "[]"
          )
        ) {
          // Case 1: Date lies within financial year
          payload.fromDate = formattedFromDate;
        } else if (
          isLeapYear(Number(yearCustome)) &&
          formattedFromDatenew === `${yearCustome}-02-29` &&
          certificateInput?.years === String(Number(yearCustome) + 1) &&
          formattedFromDatenew >= `${yearCustome}-04-01` &&
          formattedFromDatenew <= `${yearCustome}-12-31`
        ) {
          // Case 2: Handle February 29 of the leap year correctly
          payload.fromDate = formattedFromDate;
        } else {
          // Case 3: Default fallback to April 1 of the given financial year
          payload.fromDate = `${certificateInput.years}-04-01`;
        }

        // Assign toDate as the end of the financial year
        payload.toDate = `${+certificateInput.years + 1}-03-31`;

        // Determine certificate type
        const currentDate = dayjs();
        const currentYear = Number(currentDate.format("YYYY"));
        const april1stThisYear = dayjs(`${currentYear}-04-01`);

        // Financial year starts April 1st
        // Before April 1st 2026 → fin year is 25-26 (currentYear - 1)
        // On/After April 1st 2026 → fin year is 26-27 (currentYear)
        const currentFinYear = currentDate.isBefore(april1stThisYear)
            ? currentYear - 1
            : currentYear;

        const selectedYear = Number(certificateInput?.years);

        payload.type = selectedYear === currentFinYear
            ? "provsonintstcert"  // current fin year → provisional
            : "instcert";          // past fin year → interest certificate
        // const diffYear = Number(dayjs().format("YYYY")) - Number(certificateInput?.years)
        // payload.type = diffYear === 1
        //     ? "provsonintstcert"
        //     : "instcert";

        payload.finActiveStatus = matchedObject?.statusOfLoan;
      }

      payload.finActiveStatus = matchedObject?.status;
      if (type === "noc") {
        payload.finActiveStatus = "";
      }

      const res = await postsServices.addPost(
        "api/v1/report/accountstatement",
        payload
      );

      setIsLoading(false);
      if (res?.status === 200 && res?.data?.data) {
        const pdfUrl = res?.data?.data?.url;
        if (type === "soa") {
          // SOA
          if (actionType === "view") {
            viewPdf(pdfUrl);
            if(!isXsOrSm){
              setViewPdfModal({ isOpen: true, title: "Statement of Account" });
            }
          } else if (actionType === "download") {
            downloadPdf(pdfUrl, lanNumber, type);
            dispatch(
              setStatusPopup({
                status: "success",
                message: `Your Statement of Account for LAN ${selectedLoanNumber} has been downloaded successfully.`,
              })
            );
          } else if (actionType === "email") {
            dispatch(
              setStatusPopup({
                status: "success",
                message: `Statement of Account
 has been successfully sent to your registered email ID`,
              })
            );
          }
        } else if (type === "schd") {
          // schd
          if (actionType === "view") {
            viewPdf(pdfUrl);
            if(!isXsOrSm){
              setViewPdfModal({ isOpen: true, title: "Repayment Schedule" });
            }
          } else if (actionType === "download") {
            downloadPdf(pdfUrl, lanNumber, type);
            dispatch(
              setStatusPopup({
                status: "success",
                message: `Your Repayment Statement schedule for LAN ${selectedLoanNumber} has been downloaded successfully.`,
              })
            );
          } else if (actionType === "email") {
            dispatch(
              setStatusPopup({
                status: "success",
                message: `Repayment Schedule has been sent successfully to your registered Email ID`,
              })
            );
          }
        } else if (type === "noc") {
          // noc
          if (actionType === "view") {
            viewPdf(pdfUrl);
            if(!isXsOrSm){
              setViewPdfModal({
                isOpen: true,
                title: "No Objection Certificate",
              });
            }
          } else if (actionType === "download") {
            downloadPdf(pdfUrl, lanNumber, type);
            dispatch(
              setStatusPopup({
                status: "success",
                message: `No Objection Certificate has been downloaded successfully.`,
              })
            );
          } else if (actionType === "email") {
            dispatch(
              setStatusPopup({
                status: "success",
                message: `No Objection Certificate has been sent successfully to your registered Email ID`,
              })
            );
          }
        } else {
          if (actionType === "view") {
            viewPdf(pdfUrl);
            if(!isXsOrSm){
              setViewPdfModal({ isOpen: true, title: "Interest Certificate" });
            }
          } else if (actionType === "download") {
            downloadPdf(pdfUrl, lanNumber, type);
            dispatch(
              setStatusPopup({
                status: "success",
                message: `Interest Certificate has been downloaded successfully.`,
              })
            );
          } else if (actionType === "email") {
            dispatch(
              setStatusPopup({
                status: "success",
                message: `Interest Certificate has been sent successfully to your registered Email ID`,
              })
            );
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "An error occurred";
      dispatch(
        setStatusPopup({
          status: "error",
          message: errorMessage,
        })
      );
      setIsLoading(false);
    }
  };

  const welcomeLetter = async (actionType: string) => {
    try {
      const payload = {
        lan: selectedLoanNumber,
        docCategory: "WELCOMELETTER",
        cif: decrypt(userProfile?.cifNumber),
        action: actionType,
        loanCat: "term",
      };
      const res = await postsServices.addPost(
        "api/v1/report/welcomeletter",
        payload
      );
      if (res?.status === 200 && res?.data?.data) {
        const pdfUrl = res?.data?.data?.url;
        if (actionType === "view") {
          viewPdf(pdfUrl);
          if(!isXsOrSm){
            setViewPdfModal({ isOpen: true, title: "Welcome letter" });
          }
        } else if (actionType === "download") {
          downloadPdf(pdfUrl, lanNumber, "wc");
          dispatch(
            setStatusPopup({
              status: "success",
              message: `Your welcome letter has been downloaded successfully.`,
            })
          );
        } else if (actionType === "email") {
          dispatch(
            setStatusPopup({
              status: "success",
              message: `Welcome letter has been sent successfully to your registered Email ID`,
            })
          );
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "An error occurred";
      dispatch(setStatusPopup({ status: "error", message: errorMessage }));
    }
  };


  const foreclosureLetter = async (actionType: string) => {
    try {
      const matchedObject = dashboardDataSet.find(
        (data: any) => data.lanNumber === selectedLoanNumber
      )

      const payload = {
        finReference: selectedLoanNumber,
        cif: decrypt(userProfile?.cifNumber),
        action: actionType,
        finActiveStatus: matchedObject?.status,
        finTypeId: matchedObject?.loanTypeId
      };
      setIsLoading(true);
      const res = await postsServices.addPost(
        "api/v1/report/foreclosure-letter",
        payload
      );
      setIsLoading(false);
      if (res?.status === 200 && res?.data?.data) {
        const pdfUrl = res?.data?.data;
        if (actionType === "view") {
          viewPdf(pdfUrl);
          if(!isXsOrSm){
            setViewPdfModal({ isOpen: true, title: "Foreclosure Letter" });
          }
        } else if (actionType === "download") {
          downloadPdf(pdfUrl, lanNumber, "foreclosure");
          dispatch(
            setStatusPopup({
              status: "success",
              message: `Your foreclosure letter has been downloaded successfully.`,
            })
          );
        } else if (actionType === "email") {
          dispatch(
            setStatusPopup({
              status: "success",
              message: `Foreclosure letter has been sent successfully to your registered Email ID`,
            })
          );
        }
      }
    } catch (error: any) {
      setIsLoading(false);
      const errorMessage = error.response?.data?.message || "An error occurred";
      dispatch(setStatusPopup({ status: "error", message: errorMessage }));
    }
  };


  const handleAnalytics = (action: string, type: string) => {
    switch (type) {
      case "soa":
        if (action == "view") {
          trackEvent('Dash_Doc_SOA_View', decrypt(userProfile?.id), {
            label: 'Dash_Doc_SOA_View',
            category: 'Dash_Doc_SOA_View',
            action: 'Dash_Doc_SOA_View',
          });
        } else if (action == "download") {
          trackEvent('Dash_Doc_SOA_Download', decrypt(userProfile?.id), {
            label: 'Dash_Doc_SOA_Download',
            category: 'Dash_Doc_SOA_Download',
            action: 'Dash_Doc_SOA_Download',
          });
        } else if (action == "email") {
          trackEvent('Dash_Doc_SOA_Email', decrypt(userProfile?.id), {
            label: 'Dash_Doc_SOA_Email',
            category: 'Dash_Doc_SOA_Email',
            action: 'Dash_Doc_SOA_Email',
          });
        }
        break;
      case "schd":
        if (action == "view") {
          trackEvent('Dash_Doc_Repay_View', decrypt(userProfile?.id), {
            label: 'Dash_Doc_Repay_View',
            category: 'Dash_Doc_Repay_View',
            action: 'Dash_Doc_Repay_View',
          });
        } else if (action == "download") {
          trackEvent('Dash_Doc_Repay_Download', decrypt(userProfile?.id), {
            label: 'Dash_Doc_Repay_Download',
            category: 'Dash_Doc_Repay_Download',
            action: 'Dash_Doc_Repay_Download',
          });
        } else if (action == "email") {
          trackEvent('Dash_Doc_Repay_Email', decrypt(userProfile?.id), {
            label: 'Dash_Doc_Repay_Email',
            category: 'Dash_Doc_Repay_Email',
            action: 'Dash_Doc_Repay_Email',
          });
        }
        break;
      case "wc":
        if (action == "view") {
          trackEvent('Dash_Doc_Welcome_View', decrypt(userProfile?.id), {
            label: 'Dash_Doc_Welcome_View',
            category: 'Dash_Doc_Welcome_View',
            action: 'Dash_Doc_Welcome_View',
          });
        } else if (action == "download") {
          trackEvent('Dash_Doc_Welcome_Download', decrypt(userProfile?.id), {
            label: 'Dash_Doc_Welcome_Download',
            category: 'Dash_Doc_Welcome_Download',
            action: 'Dash_Doc_Welcome_Download',
          });
        } else if (action == "email") {
          trackEvent('Dash_Doc_Welcome_Email', decrypt(userProfile?.id), {
            label: 'Dash_Doc_Welcome_Email',
            category: 'Dash_Doc_Welcome_Email',
            action: 'Dash_Doc_Welcome_Email',
          });
        }
        break;
      case "cert":
        if (action == "view") {
          trackEvent('Dash_Doc_ITC_View', decrypt(userProfile?.id), {
            label: 'Dash_Doc_ITC_View',
            category: 'Dash_Doc_ITC_View',
            action: 'Dash_Doc_ITC_View',
          });
        } else if (action == "download") {
          trackEvent('Dash_Doc_ITC_Download', decrypt(userProfile?.id), {
            label: 'Dash_Doc_ITC_Download',
            category: 'Dash_Doc_ITC_Download',
            action: 'Dash_Doc_ITC_Download',
          });
        } else if (action == "email") {
          trackEvent('Dash_Doc_ITC_Email', decrypt(userProfile?.id), {
            label: 'Dash_Doc_ITC_Email',
            category: 'Dash_Doc_ITC_Email',
            action: 'Dash_Doc_ITC_Email',
          });
        }
        break;
      case "noc":
        if (action == "view") {
          trackEvent('Dash_Doc_NOC_View', decrypt(userProfile?.id), {
            label: 'Dash_Doc_NOC_View',
            category: 'Dash_Doc_NOC_View',
            action: 'Dash_Doc_NOC_View',
          });
        } else if (action == "download") {
          trackEvent('Dash_Doc_NOC_Download', decrypt(userProfile?.id), {
            label: 'Dash_Doc_NOC_Download',
            category: 'Dash_Doc_NOC_Download',
            action: 'Dash_Doc_NOC_Download',
          });
        } else if (action == "email") {
          trackEvent('Dash_Doc_NOC_Email', decrypt(userProfile?.id), {
            label: 'Dash_Doc_NOC_Email',
            category: 'Dash_Doc_NOC_Email',
            action: 'Dash_Doc_NOC_Email',
          });
        }
        break;
      default:
        console.error("Unknown document type");
    }
  };

  const handleStatementAction = (action: string, type: string) => {
    handleAnalytics(action, type);

    if (type === 'foreclosure') {
      foreclosureLetter(action);
      return;
    }

    if (!type.includes(`wc`)) {
      if (type.includes(`noc`)) {
        const matchedObject = dashboardDataSet?.find(
          (data: any) => data?.lanNumber === selectedLoanNumber
        );
        const maturityYear = +dateParser(matchedObject?.maturityDate, "YY");
        const currentYear = +dateParser("", "YY");
        if (maturityYear > currentYear) {
          dispatch(
            setStatusPopup({
              status: "info",
              message: "'No objection certificate' not available",
            })
          );
        } else {
          accountStatement(action, type);
        }
      } else {
        accountStatement(action, type);
      }
    } else {
      welcomeLetter(action);
    }
  };

  const viewPdf = async (pdfUrl: string) => {
    setIsLoading(true);
    try {
      const httpsUrl = pdfUrl.replace(/^http:\/\//i, "https://");
      const response = await fetch(httpsUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      if (isXsOrSm) {
        if(!isXsOrSm){
         setViewPdfModal((prev) => ({ ...prev, isOpen: true }));
        }
        window.open(url, "_blank");
        setIsLoading(false);
      } else {
        setPdfUrlAccountStatement(pdfUrl);
        if(!isXsOrSm){
          setViewPdfModal((prev) => ({ ...prev, isOpen: true }));
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching PDF:", error);
      setIsLoading(false);
    }
  };

  const downloadPdf = async (pdfUrl: string, lan: string, type: string) => {
    try {
      const httpsUrl = pdfUrl.replace(/^http:\/\//i, "https://");
      const response = await fetch(httpsUrl, {
        method: "GET",
        mode: "cors",
        referrerPolicy: "no-referrer",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch PDF: ${response.statusText} - ${errorText}`
        );
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/pdf")) {
        const errorText = await response.text();
        throw new Error(
          `The fetched file is not a PDF. Response: ${errorText}`
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.download = `${lan}_${type}.pdf`;

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      // Handle error state or display error message
    } finally {
      setIsLoading(false);
    }
  };
  const handleDateChange = (date: Date | null) => {
    if (date !== null) {
      setToDate(date); // Update toDate state when date is not null
    }
    // Optionally handle the case when date is null, if needed
  };
  const handleFromChange = (date: Date | null) => {
    if (date !== null) {
      setFromDate(date);
    }
  };

  const handleCertificateChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setCertificateInput({
      ...certificateInput,
      [e.target.name]: e.target.value,
    });
  };

  const activeValue = useMemo(() => {
    if (isActive == "M") return true;
    if (isActive == "C") return true;
    if (isActive == "W") return true;
    if (isActive == "E") return true;
    if (isActive == "S") return true;

    return false;
  }, [isActive]);

  return (
    <Box>
      <Grid container style={{ padding: "0px" }}>
        {!isXsOrSm && (
          <Grid item xs={0} sm={0} md={4} lg={4}>
            <HeadingLeftRight titleone="Documents" displayC="true" />
          </Grid>
        )}

        <Grid item xs={12} sm={12} md={0} lg={0}>
          {isXsOrSm && (
            <HeadingLeftRight titleone="Documents" displayC="true" />
          )}
          <Grid item xs={12} sm={0} md={8} lg={8}>
              {isXsOrSm && (
                <Card
                  sx={{
                    background: "#fff",
                    boxShadow: "none",
                    borderRadius: "8px",
                    padding: isXsOrSm ? "8px 0" : "0",
                    marginBottom: isXsOrSm ? "10px" : "0",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", pl: 2 }}>
                    <AccountSelectorDropDown
                      labelText="Loan account number"
                      buttonText=" N/A"
                      isActive={""}
                      backgroundColor="transparent"
                      borderWidth="0px"
                      marginTop="0px"
                    />
                  </Box>
                </Card>
              )}
          </Grid>
          <Grid container spacing={1} mb={2}>
            <Grid item xs={12} sm={0} md={8} lg={8}></Grid>
            <Grid item xs={6} sm={6} md={2} lg={2}>
              {fromDate && (
                <BasicDatePicker
                  label="From Date"
                  width="100%"
                  required
                  value={fromDate ? dayjs(fromDate) : null} // Convert Date to dayjs if needed
                  onChange={handleFromChange}
                  minDate={minDate ? dayjs(minDate) : undefined} // Set minDate based on minDate state
                  maxDate={maxDate ? dayjs(maxDate) : undefined}
                />
              )}
            </Grid>
            <Grid item xs={6} sm={6} md={2} lg={2}>
              <BasicDatePicker
                label="To Date"
                width="100%"
                required
                onChange={handleDateChange}
                value={toDate ? dayjs(toDate) : null}
                minDate={minDate ? dayjs(minDate) : undefined}
                maxDate={maxDate ? dayjs(maxDate) : undefined}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Card
        sx={{
          ...(isXsOrSm && {
            background: "#F8F7FA",
            borderWidth: "0px",
            borderRadius: "name",
            boxShadow: "none",
          }),
        }}
      >
        <Grid
          container
          spacing={1}
          className={`statement-container ${isXsOrSm ? "statement-container-mobile" : ""
            }`}
        >
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid
              container
              className={`sc-row ${isXsOrSm ? "sc-row-mobile" : ""}`}
              sx={{ marginBottom: "20px" }}
            >
              <Grid xs={12} sm={12} md={7} lg={7}>
                <Typography className="sm-col-1">
                  Statement of Account
                </Typography>
              </Grid>
              {isXsOrSm && (
                <Grid item xs={12} sm={12} md={7} lg={7}>
                  <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
                </Grid>
              )}
              <Grid item xs={12} sm={12} md={5} lg={5}>
                <Grid container>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <IconButton
                      onClick={() => handleStatementAction("view", "soa")}
                    >
                      <img src={ViewIcon} alt="View Icon" />
                      <Typography>View</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <IconButton
                      onClick={() => handleStatementAction("download", "soa")}
                    >
                      <img src={DownloadIcon} alt="Download Icon" />
                      <Typography>Download</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <Box sx={{ float: "left", marginLeft: "29px" }}>
                      <IconButton
                        onClick={() => handleStatementAction("email", "soa")}
                      >
                        <img
                          src={EmailIcon}
                          alt="Email Icon"
                          className="pointer"
                        />
                        <Typography className="pointer">Email</Typography>
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              className={`sc-row ${isXsOrSm ? "sc-row-mobile" : ""}`}
            >
              <Grid item xs={12} sm={12} md={7} lg={7}>
                <Typography className="sm-col-1">
                  Repayment Schedule
                </Typography>
              </Grid>
              {isXsOrSm && (
                <Grid item xs={12} sm={12} md={7} lg={7}>
                  <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
                </Grid>
              )}
              <Grid item xs={12} sm={12} md={5} lg={5}>
                <Grid container>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    {" "}
                    <IconButton
                      onClick={() => handleStatementAction("view", "schd")}
                    >
                      <img src={ViewIcon} alt="View Icon" />
                      <Typography>View</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    {" "}
                    <IconButton
                      onClick={() => handleStatementAction("download", "schd")}
                    >
                      <img src={DownloadIcon} alt="Download Icon" />
                      <Typography>Download</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <Box sx={{ float: "left", marginLeft: "29px" }}>
                      <IconButton
                        sx={{ display: "flex", alignItems: "center" }}
                        onClick={() => handleStatementAction("email", "schd")}
                      >
                        <img
                          src={EmailIcon}
                          alt="Email Icon"
                          className="pointer"
                        />
                        <Typography className="pointer">Email</Typography>
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              className={`sc-row ${isXsOrSm ? "sc-row-mobile" : ""}`}
            >
              <Grid item xs={12} sm={12} md={7} lg={7}>
                <Typography className="sm-col-1">Welcome Letter</Typography>
              </Grid>
              {isXsOrSm && (
                <Grid item xs={12} sm={12} md={7} lg={7}>
                  <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
                </Grid>
              )}
              <Grid item xs={12} sm={12} md={5} lg={5}>
                <Grid container>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    {" "}
                    <IconButton
                      onClick={() => handleStatementAction("view", "wc")}
                    >
                      <img src={ViewIcon} alt="View Icon" />
                      <Typography>View</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <IconButton
                      onClick={() => handleStatementAction("download", "wc")}
                    >
                      <img src={DownloadIcon} alt="Download Icon" />
                      <Typography>Download</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <Box sx={{ float: "left", marginLeft: "29px" }}>
                      {" "}
                      <IconButton
                        onClick={() => handleStatementAction("email", "wc")}
                      >
                        <img
                          src={EmailIcon}
                          alt="Email Icon"
                          className="pointer"
                        />
                        <Typography className="pointer">Email</Typography>
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {isForeclosureAllowedValue && (
              <Grid
                container
                className={`sc-row ${isXsOrSm ? "sc-row-mobile" : ""}`}
              >
                <Grid item xs={12} sm={12} md={7} lg={7}>
                  <Typography className="sm-col-1">Foreclosure Letter</Typography>

                  <Typography
                    sx={{ fontSize: "12px", color: "#00b3b5", fontWeight: "bold", textDecoration: "underline", cursor: "pointer" }}
                    onClick={() => setViewDetailsModal(true)}
                  >
                    View Details
                  </Typography>

                </Grid>
                {isXsOrSm && (
                  <Grid item xs={12} sm={12} md={7} lg={7}>
                    <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
                  </Grid>
                )}
                <Grid item xs={12} sm={12} md={5} lg={5}>
                  <Grid container>
                    <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                      {" "}
                      <IconButton
                        onClick={() => handleStatementAction("view", "foreclosure")}
                      >
                        <img src={ViewIcon} alt="View Icon" />
                        <Typography>View</Typography>
                      </IconButton>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                      <IconButton
                        onClick={() => handleStatementAction("download", "foreclosure")}
                      >
                        <img src={DownloadIcon} alt="Download Icon" />
                        <Typography>Download</Typography>
                      </IconButton>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                      <Box sx={{ float: "left", marginLeft: "29px" }}>
                        {" "}
                        <IconButton
                          onClick={() => handleStatementAction("email", "foreclosure")}
                        >
                          <img
                            src={EmailIcon}
                            alt="Email Icon"
                            className="pointer"
                          />
                          <Typography className="pointer">Email</Typography>
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {activeValue && (
              <Grid
                container
                className={`sc-row ${isXsOrSm ? "sc-row-mobile" : ""}`}
              >
                <Grid item xs={12} sm={12} md={7} lg={7}>
                  <Typography className="sm-col-1">
                    No Objection Certificate
                  </Typography>
                </Grid>
                {isXsOrSm && (
                  <Grid item xs={12} sm={12} md={7} lg={7}>
                    <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
                  </Grid>
                )}
                <Grid item xs={12} sm={12} md={5} lg={5}>
                  <Grid container>
                    <Grid
                      item
                      xs={4}
                      sm={4}
                      md={4}
                      lg={4}
                      className="icon-lable"
                    >
                      {" "}
                      <IconButton
                        onClick={() => handleStatementAction("view", "noc")}
                      >
                        <img src={ViewIcon} alt="View Icon" />
                        <Typography>View</Typography>
                      </IconButton>
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      sm={4}
                      md={4}
                      lg={4}
                      className="icon-lable"
                    >
                      {" "}
                      <IconButton
                        onClick={() => handleStatementAction("download", "noc")}
                      >
                        <img src={DownloadIcon} alt="Download Icon" />
                        <Typography>Download</Typography>
                      </IconButton>
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      sm={4}
                      md={4}
                      lg={4}
                      className="icon-lable"
                    >
                      <Box sx={{ float: "left", marginLeft: "29px" }}>
                        {" "}
                        <IconButton
                          onClick={() => handleStatementAction("email", "noc")}
                        >
                          <img
                            src={EmailIcon}
                            alt="Email Icon"
                            className="pointer"
                          />
                          <Typography className="pointer">Email</Typography>
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Box className={`sc-row ${isXsOrSm ? "sc-row-mobile" : ""}`}>
              <Grid container>
                <Grid item xs={12} sm={12} md={7} lg={7}>
                  <Typography className="sm-col-1">
                    Interest Certificate
                  </Typography>
                </Grid>
                {isXsOrSm && (
                  <Grid item xs={12} sm={12} md={7} lg={7}>
                    <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
                  </Grid>
                )}
                <Grid item xs={12} sm={12} md={5} lg={5}>
                  <Grid container>
                    <Grid
                      item
                      xs={4}
                      sm={4}
                      md={4}
                      lg={4}
                      className="icon-lable"
                    >
                      <span className="pointer">
                        <IconButton
                          onClick={() => handleStatementAction("view", "cert")}
                        >
                          <img src={ViewIcon} alt="View Icon" />
                          <Typography>View</Typography>
                        </IconButton>
                      </span>
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      sm={4}
                      md={4}
                      lg={4}
                      className="icon-lable"
                    >
                      <span className="pointer">
                        <IconButton
                          onClick={() =>
                            handleStatementAction("download", "cert")
                          }
                        >
                          <img src={DownloadIcon} alt="Download Icon" />
                          <Typography>Download</Typography>
                        </IconButton>
                      </span>
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      sm={4}
                      md={4}
                      lg={4}
                      className="icon-lable"
                    >
                      <Box sx={{ float: "left", marginLeft: "29px" }}>
                        <IconButton
                          onClick={() => handleStatementAction("email", "cert")}
                        >
                          <img
                            src={EmailIcon}
                            alt="Email Icon"
                            className="pointer"
                          />
                          <Typography className="pointer">Email</Typography>
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {!isXsOrSm && (
                <Box sx={{ marginTop: "12px" }}>
                  <Divider />
                </Box>
              )}
              <Grid container className="mt-20">
                <Grid item xs={12} sm={3} md={3} lg={3}>
                  <InputField
                    select
                    fullWidth
                    name="years"
                    value={certificateInput?.years}
                    label="Years"
                    onChange={(e) => handleCertificateChange(e)}
                    autoComplete="off"
                    sx={{
                      '.MuiOutlinedInput-input': {
                        background: "transparent"
                      }
                    }}
                  >
                    {finYearsOptions?.map((option: any) => (
                      <MenuItem selected value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </InputField>
                </Grid>
              </Grid>
            </Box>
            <PdfPasswordInfo />
          </Grid>
        </Grid>
      </Card>

      <>
        <ModalPopup
          isOpen={viewPdfModal?.isOpen}
          onClose={() => setViewPdfModal({ ...viewPdfModal, isOpen: false })}
          title={viewPdfModal?.title}
          style={{ height: "100%", overflowX: "hidden", minWidth: "70%" }}
          onNavigate={() => {
            throw new Error("Function not implemented.");
          }}
        >
          <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
          <PDFViewer pdfUrl={pdfUrlAccountStatement} />
        </ModalPopup>


        <ModalPopup
          isOpen={viewDetailsModal}
          onClose={() => setViewDetailsModal(false)}
          title="Appended is a quick synopsis of the foreclosure process for your reference. "
          style={{ minWidth: "50%" }}
          onNavigate={() => { }}
          childrenAction={
            <a
              href={require("../../../assets/documents/form.pdf")}
              download
              style={{ textDecoration: 'none' }}
            >
              <button
                style={{
                  background: '#00b3b5',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 18px',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  float: 'right',
                  marginTop: '-38px',
                  marginRight: '40px',
                }}
              >
                {isXsOrSm ? "Download" : "Download Foreclosure Form"}
              </button>
            </a>
          }
        >
          <Box sx={{ fontSize: "15px", color: "#333" }}>
            <ul>
              <li>Download and Fill out the education loan foreclosure request form for each account and share it with us to initiate the foreclosure procedure.</li>
              <li>Once we receive the updated foreclosure request form, we will share all the foreclosure details within 12 working days from the date of submission of the form.</li>
              <li>In addition, you will also receive a payment link from the following email ID – <a href="mailto:noreply@avanse.com">noreply@avanse.com</a>. You can pay online with the help of this link. However, if you wish to make an offline transaction, the details will be shared with you.</li>
              <li>The foreclosure payment will be updated in our system. This process will take 15 working days, after which we will share the NOC. Please note the original documents submitted at the beginning of the loan process (<i>if any</i>) will be returned within 30 working days from the day of your loan closure.</li>
            </ul>
            <br />
            <b>Here are some of the important things to keep in mind while filling out the form:</b>
            <ul>
              <li><b>Personal details:</b> Date, full name, address, registered mobile number, and email ID to be filled in correctly</li>
              <li><b>Loan details:</b> Branch name, Loan Account Number (LAN) and PAN must be filled in appropriately</li>
              <li><b>In Point Number 8</b>, you need to select the source of funds for foreclosing the loan</li>
              <li><b>In Point Number 9</b>, please capture the preferred mode of payment</li>
              <li><b>Point Number 14</b> is about insurance policy details (if any)</li>
              <li>And lastly, at the end, request the relevant person to sign the document before submitting the same.</li>
            </ul>
            <br />
            <b>Here are some additional details for your reference:</b>
            <ul>
              <li>Please ensure that the payment is made from the applicant/co-applicant's Indian bank account or NRE/NRO account, and we accept payment in Indian currency (INR = ₹) only.</li>
              <li>You can only foreclose or make part payments post 06 months from the date of the first instalment deduction.</li>
              <li>INR 2,950/- (INR 2,500 + 18% GST) is applicable as Document Retrieval Charges. For more details, visit <a href="https://www.avanse.com/responsible-lending" target="_blank">Responsible Lending</a>.</li>
              <li>Charges for updating and handling the loan account would be applicable as per the Schedule of Charges. For more details, visit <a href="https://www.avanse.com/schedule-of-charges" target="_blank">Schedule Of Charges - All Products - Avanse Financial Services</a>.</li>
            </ul>
          </Box>
        </ModalPopup>
      </>


      <LoaderBackdrop open={isLoading} />
    </Box>
  );
}
